import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Circle, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { Progress } from "./ui/progress";
import { HelpLink } from "./HelpTip";

// Prompt 19 PART A — "Getting started" checklist, computed live server-side.
const HELP_ANCHOR = {
  connect_gl: "connect-your-accounting-system",
  receiving_gl: "receiving-gl",
  billing_email: "billing-email",
  approve_vendor: "approving-vendors",
  map_items: "mapping-items",
  first_sync: "invoice-statuses",
};

function Step({ s }) {
  return (
    <li className={`flex items-center gap-4 border p-4 transition-colors ${
          s.done ? "border-emerald-300 bg-emerald-50/50" : "border-neutral-200 hover:border-neutral-900"}`}
        data-testid={`onboarding-step-${s.key}`} data-done={s.done ? "true" : "false"}>
      <div className={`shrink-0 w-7 h-7 rounded-full border flex items-center justify-center ${
            s.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-neutral-400 text-neutral-400"}`}>
        {s.done ? <Check size={14} /> : <Circle size={10} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm flex items-center gap-2">
          {s.title}
          <HelpLink anchor={HELP_ANCHOR[s.key]} label={s.title} testid={`onboarding-help-${s.key}`} />
        </div>
        <div className="text-xs text-neutral-600 mt-0.5">{s.hint}</div>
      </div>
      {!s.done && (
        <Link to={s.href} data-testid={`onboarding-go-${s.key}`}
              className="shrink-0 text-xs uppercase tracking-widest flex items-center gap-1 text-neutral-900 hover:underline">
          Go <ChevronRight size={12} />
        </Link>
      )}
    </li>
  );
}

export default function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/onboarding/checklist");
        if (alive) setData(r.data);
      } catch (_e) { /* silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!data || data.dismissed) return null;

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/checklist/dismiss");
      setData({ ...data, dismissed: true });
    } finally { setBusy(false); }
  };

  return (
    <section className="card-flat p-6 border-l-4 border-l-neutral-900" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-xl font-semibold">Getting started</h2>
          <p className="text-sm text-neutral-600 mt-1" data-testid="onboarding-progress-text">
            {data.done_count} of {data.total} complete
          </p>
        </div>
        {!data.all_done && (
          <button type="button" onClick={dismiss} disabled={busy}
                  className="text-xs text-neutral-500 underline hover:text-neutral-900"
                  data-testid="onboarding-hide">
            Hide checklist
          </button>
        )}
      </div>
      <Progress value={(data.done_count / data.total) * 100}
                className="h-1.5 rounded-none bg-neutral-200 mb-5 [&>div]:bg-neutral-900"
                data-testid="onboarding-progress-bar" />
      <ol className="space-y-3">
        {data.steps.map((s) => <Step key={s.key} s={s} />)}
      </ol>
      {data.all_done && (
        <div className="mt-5 flex items-center justify-between gap-4 border-t border-neutral-200 pt-4">
          <div className="font-display font-semibold" data-testid="onboarding-all-set">You're all set</div>
          <button type="button" onClick={dismiss} disabled={busy}
                  className="btn-primary" data-testid="onboarding-dismiss">
            Dismiss
          </button>
        </div>
      )}
    </section>
  );
}
